/**
 * Swift code emitter - Functional implementation
 * Code formatting and emission utilities for Swift/Objective-C
 */

export interface SwiftCodeEmitterOptions {
  /**
   * Number of spaces for indentation
   */
  indentSize?: number;
  /**
   * Whether to use public access by default
   */
  defaultPublic?: boolean;
}

export interface SwiftCodeEmitter {
  /**
   * Generates standard file header comment
   */
  generateFileHeader(): string;

  /**
   * Generates Swift import statements
   */
  generateSwiftImports(imports: string[]): string;

  /**
   * Generates Objective-C import statements
   */
  generateObjCImports(imports: string[]): string;

  /**
   * Generates complete Swift file content with header and imports
   */
  generateSwiftFile(
    imports: string[],
    content: string,
    moduleComment?: string,
  ): string;

  /**
   * Generates complete Objective-C file content with header and imports
   */
  generateObjCFile(imports: string[], content: string): string;

  /**
   * Indents code block by specified number of spaces
   */
  indent(code: string, spaces?: number): string;

  /**
   * Wraps content in braces with proper indentation
   */
  wrapInBraces(content: string, inline?: boolean): string;

  /**
   * Generates Swift documentation comment
   */
  generateSwiftDocComment(comment: string): string;

  /**
   * Generates multi-line Swift documentation comment
   */
  generateSwiftMultilineDocComment(lines: string[]): string;

  /**
   * Generates array literal in Swift syntax
   */
  generateSwiftArray(items: string[]): string;

  /**
   * Generates array literal in Objective-C syntax
   */
  generateObjCArray(items: string[]): string;

  /**
   * Generates MARK comment for code organization
   */
  generateMarkComment(title: string): string;

  /**
   * Capitalizes first character of a string
   */
  capitalizeFirst(str: string): string;

  /**
   * Converts camelCase to PascalCase
   */
  toPascalCase(str: string): string;

  /**
   * Generates method parameter list
   */
  generateParameterList(
    params: Array<{ name: string; type: string }>,
    includeFirst?: boolean,
  ): string;

  /**
   * Generates Swift function signature
   */
  generateSwiftFunctionSignature(
    name: string,
    params: Array<{ name: string; type: string }>,
    returnType: string,
    isAsync?: boolean,
    throws?: boolean,
    access?: "public" | "private" | "internal",
  ): string;

  /**
   * Generates Objective-C method signature
   */
  generateObjCMethodSignature(
    name: string,
    params: Array<{ name: string; type: string; label?: string }>,
    returnType: string,
  ): string;

  /**
   * Wraps value in Objective-C @ literal
   */
  wrapObjCLiteral(value: string | number | boolean): string;

  /**
   * Generates property declaration in Swift
   */
  generateSwiftProperty(
    name: string,
    type: string,
    isLet?: boolean,
    isPublic?: boolean,
    defaultValue?: string,
  ): string;

  /**
   * Generates struct declaration in Swift
   */
  generateSwiftStruct(
    name: string,
    properties: Array<{ name: string; type: string; comment?: string }>,
    protocols?: string[],
    isPublic?: boolean,
  ): string;

  /**
   * Generates protocol declaration in Swift
   */
  generateSwiftProtocol(
    name: string,
    methods: string[],
    properties?: string[],
    isPublic?: boolean,
  ): string;

  /**
   * Generates extension declaration in Swift
   */
  generateSwiftExtension(
    typeName: string,
    content: string,
    protocols?: string[],
  ): string;

  /**
   * Generates type alias in Swift
   */
  generateSwiftTypeAlias(
    alias: string,
    target: string,
    isPublic?: boolean,
    comment?: string,
  ): string;
}

/**
 * Creates a Swift code emitter with the given options
 */
export function createSwiftCodeEmitter(
  options: SwiftCodeEmitterOptions = {},
): SwiftCodeEmitter {
  const { indentSize = 4, defaultPublic = true } = options;

  function generateFileHeader(): string {
    return `// This file is automatically generated by brick-codegen
// Do not edit manually - regenerate using: brick-codegen`;
  }

  function generateSwiftImports(imports: string[]): string {
    if (imports.length === 0) {
      return "";
    }
    return imports.map((imp) => `import ${imp}`).join("\n");
  }

  function generateObjCImports(imports: string[]): string {
    if (imports.length === 0) {
      return "";
    }
    return imports.map((imp) => `#import ${imp}`).join("\n");
  }

  function generateSwiftFile(
    imports: string[],
    content: string,
    moduleComment?: string,
  ): string {
    const header = generateFileHeader();
    const importsSection = generateSwiftImports(imports);
    const comment = moduleComment ? `\n/// ${moduleComment}` : "";

    return `${header}

${importsSection}${comment}

${content}
`;
  }

  function generateObjCFile(imports: string[], content: string): string {
    const header = generateFileHeader();
    const importsSection = generateObjCImports(imports);

    return `${header}

${importsSection}

${content}
`;
  }

  function indent(code: string, spaces: number = indentSize): string {
    const indentation = " ".repeat(spaces);
    return code
      .split("\n")
      .map((line) => (line.trim() ? `${indentation}${line}` : line))
      .join("\n");
  }

  function wrapInBraces(content: string, inline: boolean = false): string {
    if (inline) {
      return `{ ${content} }`;
    }
    return `{\n${indent(content)}\n}`;
  }

  function generateSwiftDocComment(comment: string): string {
    return `/// ${comment}`;
  }

  function generateSwiftMultilineDocComment(lines: string[]): string {
    return `/**\n${lines.map((line) => ` * ${line}`).join("\n")}\n */`;
  }

  function generateSwiftArray(items: string[]): string {
    if (items.length === 0) {
      return "[]";
    }
    return `[${items.join(", ")}]`;
  }

  function generateObjCArray(items: string[]): string {
    if (items.length === 0) {
      return "@[]";
    }
    return `@[${items.join(", ")}]`;
  }

  function generateMarkComment(title: string): string {
    return `// MARK: - ${title}`;
  }

  function capitalizeFirst(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  function toPascalCase(str: string): string {
    return capitalizeFirst(str);
  }

  function generateParameterList(
    params: Array<{ name: string; type: string }>,
    includeFirst: boolean = true,
  ): string {
    if (params.length === 0) {
      return "";
    }

    return params
      .map((param, index) => {
        if (index === 0 && includeFirst) {
          return `${param.name}: ${param.type}`;
        }
        return `${param.name}: ${param.type}`;
      })
      .join(", ");
  }

  function generateSwiftFunctionSignature(
    name: string,
    params: Array<{ name: string; type: string }>,
    returnType: string,
    isAsync: boolean = false,
    throws: boolean = false,
    access: "public" | "private" | "internal" = defaultPublic
      ? "public"
      : "internal",
  ): string {
    const paramList = generateParameterList(params);
    const asyncKeyword = isAsync ? " async" : "";
    const throwsKeyword = throws ? " throws" : "";
    const returnPart = returnType !== "Void" ? ` -> ${returnType}` : "";

    return `${access} func ${name}(${paramList})${asyncKeyword}${throwsKeyword}${returnPart}`;
  }

  function generateObjCMethodSignature(
    name: string,
    params: Array<{ name: string; type: string; label?: string }>,
    returnType: string,
  ): string {
    if (params.length === 0) {
      return `- (${returnType})${name}`;
    }

    const firstParam = params[0]!; // Safe because we checked length above
    const restParams = params.slice(1);

    const firstPart = `- (${returnType})${name}:(${firstParam.type})${firstParam.name}`;
    const restParts = restParams
      .map(
        (param) => `${param.label || param.name}:(${param.type})${param.name}`,
      )
      .join(" ");

    return restParts ? `${firstPart} ${restParts}` : firstPart;
  }

  function wrapObjCLiteral(value: string | number | boolean): string {
    if (typeof value === "string") {
      return `@"${value}"`;
    }
    if (typeof value === "boolean") {
      return value ? "@YES" : "@NO";
    }
    return `@(${value})`;
  }

  function generateSwiftProperty(
    name: string,
    type: string,
    isLet: boolean = true,
    isPublic: boolean = defaultPublic,
    defaultValue?: string,
  ): string {
    const access = isPublic ? "public " : "";
    const keyword = isLet ? "let" : "var";
    const value = defaultValue ? ` = ${defaultValue}` : "";
    return `${access}${keyword} ${name}: ${type}${value}`;
  }

  function generateSwiftStruct(
    name: string,
    properties: Array<{ name: string; type: string; comment?: string }>,
    protocols: string[] = ["Codable"],
    isPublic: boolean = defaultPublic,
  ): string {
    const access = isPublic ? "public " : "";
    const conformance = protocols.length > 0 ? `: ${protocols.join(", ")}` : "";

    const propsWithComments = properties
      .map((prop) => {
        const comment = prop.comment
          ? `    ${generateSwiftDocComment(prop.comment)}\n`
          : "";
        return `${comment}    ${generateSwiftProperty(prop.name, prop.type, true, true)}`;
      })
      .join("\n");

    return `${access}struct ${name}${conformance} {
${propsWithComments}
}`;
  }

  function generateSwiftProtocol(
    name: string,
    methods: string[],
    properties: string[] = [],
    isPublic: boolean = defaultPublic,
  ): string {
    const access = isPublic ? "public " : "";
    const propsSection =
      properties.length > 0
        ? `${properties.map((p) => `    ${p}`).join("\n")}\n\n`
        : "";
    const methodsSection = methods.map((m) => `    ${m}`).join("\n\n");

    return `${access}protocol ${name} {
${propsSection}${methodsSection}
}`;
  }

  function generateSwiftExtension(
    typeName: string,
    content: string,
    protocols: string[] = [],
  ): string {
    const conformance = protocols.length > 0 ? `: ${protocols.join(", ")}` : "";

    return `extension ${typeName}${conformance} {
${indent(content)}
}`;
  }

  function generateSwiftTypeAlias(
    alias: string,
    target: string,
    isPublic: boolean = defaultPublic,
    comment?: string,
  ): string {
    const access = isPublic ? "public " : "";
    const commentLine = comment
      ? `${generateSwiftDocComment(comment)}\n`
      : "";
    return `${commentLine}${access}typealias ${alias} = ${target}`;
  }

  return {
    generateFileHeader,
    generateSwiftImports,
    generateObjCImports,
    generateSwiftFile,
    generateObjCFile,
    indent,
    wrapInBraces,
    generateSwiftDocComment,
    generateSwiftMultilineDocComment,
    generateSwiftArray,
    generateObjCArray,
    generateMarkComment,
    capitalizeFirst,
    toPascalCase,
    generateParameterList,
    generateSwiftFunctionSignature,
    generateObjCMethodSignature,
    wrapObjCLiteral,
    generateSwiftProperty,
    generateSwiftStruct,
    generateSwiftProtocol,
    generateSwiftExtension,
    generateSwiftTypeAlias,
  };
}
